<template>
    <section class="booking-form-card" aria-labelledby="booking-options-title">
        <div class="booking-section-heading">
            <span class="booking-section-icon"><feather type="sliders" aria-hidden="true" /></span>
            <div>
                <h3 id="booking-options-title">{{ $t("reservationForm.details") }}</h3>
                <p>{{ $t("reservationForm.optional") }}</p>
            </div>
        </div>
        <Commentary :restaurant_id="restaurantId" :reservation="reservation" :restaurant="restaurant" :formErrors="{ errors }" :widget="widget" />
        <p v-if="isWaiting && files.length" class="booking-alert" role="alert">{{ $t("reservationForm.waitingFiles") }}</p>
        <FileUploader v-if="!isWaiting || files.length" :value="files" @input="$emit('update-files', $event)" :errors="fileErrors" />
        <div v-if="employees.length && !isWaiting" class="booking-employee">
            <label for="booking-employee">{{ $tl("labels.booking.reservations.savedBy") }} *</label>
            <select id="booking-employee" class="form-select" v-model="employeeId" required :aria-invalid="Boolean(errors.user_id)">
                <option value="">—</option>
                <option v-for="user in employees" :key="user.id" :value="user.id">{{ user.firstname }} {{ user.lastname }}</option>
            </select>
            <small class="text-danger">{{ errors.user_id?.[0] }}</small>
        </div>
        <div v-if="!isWaiting && showPayment" class="booking-payment">
            <h4>{{ $t("reservationForm.payment") }}</h4>
            <fieldset v-if="canSecure" class="booking-guarantees">
                <legend class="visually-hidden">{{ $tl("labels.booking.reservations.secure") }}</legend>
                <label class="booking-checkbox"
                    ><input type="radio" name="booking-guarantee" :value="null" v-model="reservation.option_bank" />{{ $tl("labels.none") }}</label
                >
                <label v-for="option in guaranteeOptions" :key="option.value" class="booking-checkbox">
                    <input
                        type="radio"
                        name="booking-guarantee"
                        :value="option.value"
                        v-model="reservation.option_bank"
                        :disabled="!option.available" />
                    {{ option.label }}
                    <small v-if="!option.available">{{ option.reason }}</small>
                </label>
            </fieldset>
            <div v-if="reservation.option_bank === OPTION_BANK_PAYMENT.value" class="booking-payment-amount">
                <label for="booking-amount">{{ $tl("labels.booking.reservations.payment.amountToPay") }}</label>
                <input
                    id="booking-amount"
                    type="number"
                    class="form-control"
                    min="0.01"
                    step="0.01"
                    required
                    v-model.number="reservation.amount_to_pay"
                    @input="customAmount = true" />
                <small class="text-danger">{{ errors.amount_to_pay?.[0] }}</small>
                <p class="booking-help">{{ $tl("infos.booking.reservations.payment.willBeSendOnSave") }}</p>
            </div>
            <p v-if="reservation.option_bank === OPTION_BANK_FOOTPRINT.value" class="booking-help">{{ $t("reservationForm.guaranteeHint") }}</p>
            <label v-if="reservation.option_bank === null && canConfirm" class="booking-checkbox booking-confirmation">
                <input type="checkbox" v-model="reservation.send_confirmation" />{{ $tl("labels.booking.reservations.confirmation.send") }}
            </label>
            <p v-else-if="confirmationHint" class="booking-help">{{ confirmationHint }}</p>
            <small v-if="errors.option_bank" class="text-danger">{{ errors.option_bank[0] }}</small>
        </div>
    </section>
</template>

<script>
import Commentary from "./commentaryReservations.vue";
import FileUploader from "../FileUploader/FileUploader.vue";
import OptionBankEnum from "../../../mixins/enums/booking/OptionBankEnum";
import { reservationExtrasTotal } from "./reservationFormState.mjs";

export default {
    components: { Commentary, FileUploader },
    mixins: [OptionBankEnum],
    props: {
        isWaiting: Boolean,
        reservation: { type: Object, required: true },
        restaurant: { type: Object, required: true },
        restaurantId: { required: true },
        widget: { type: Object, required: true },
        employees: { type: Array, default: () => [] },
        files: { type: Array, default: () => [] },
        errors: { type: Object, default: () => ({}) },
    },
    emits: ["update-files"],
    data: () => ({ customAmount: false }),
    computed: {
        employeeId: {
            get() {
                return this.reservation.user_id ?? "";
            },
            set(value) {
                this.reservation.user_id = value === "" ? null : value;
            },
        },
        total() {
            return reservationExtrasTotal(this.reservation);
        },
        suggestedAmount() {
            return Math.round(this.total * Number(this.reservation.service?.prepayment_percent ?? 100)) / 100;
        },
        fileErrors() {
            return Object.entries(this.errors)
                .filter(([key]) => key === "files" || key.startsWith("files."))
                .flatMap(([, messages]) => messages);
        },
        canSecure() {
            if (!this.reservation.slot?.hour_start) return false;
            return (
                this.setHourOnDateTime(this.reservation.slot.hour_start, this.reservation.reservation_date).minus({
                    hours: Number(this.widget.option_bank_short_delay ?? 1),
                    minutes: 30,
                }) > this.getDateTime()
            );
        },
        confirmationChannel() {
            return this.restaurant.payment_notif_settings?.send_confirmation_by || null;
        },
        canConfirm() {
            return this.hasContact(this.confirmationChannel);
        },
        confirmationHint() {
            if (this.reservation.option_bank !== null || !this.confirmationChannel || this.canConfirm) return null;
            return this.$t(this.confirmationChannel === "sms" ? "reservationForm.confirmationNeedsPhone" : "reservationForm.confirmationNeedsEmail");
        },
        showPayment() {
            return this.canSecure || this.reservation.option_bank !== null || Boolean(this.confirmationChannel) || Boolean(this.errors.option_bank);
        },
        guaranteeOptions() {
            return [
                {
                    value: this.OPTION_BANK_FOOTPRINT.value,
                    label: this.$tl("labels.booking.reservations.footprint.title"),
                    channel: this.restaurant.payment_notif_settings?.ask_footprint_by,
                },
                {
                    value: this.OPTION_BANK_PAYMENT.value,
                    label: this.$tl("labels.booking.reservations.payment.title"),
                    channel: this.restaurant.payment_notif_settings?.ask_payment_by,
                },
            ].map((option) => ({
                ...option,
                available: this.hasContact(option.channel),
                reason: this.$tl(option.channel === "sms" ? "labels.form.phoneNumber" : "labels.form.email") + " *",
            }));
        },
    },
    methods: {
        hasContact(channel) {
            return channel === "sms" ? Boolean(this.reservation.client.phone.raw) : channel === "mail" && Boolean(this.reservation.client.email);
        },
    },
    watch: {
        suggestedAmount: {
            immediate: true,
            handler(value) {
                if (!this.customAmount) this.reservation.amount_to_pay = value;
            },
        },
        "reservation.service.id"() {
            this.customAmount = false;
            this.reservation.amount_to_pay = this.suggestedAmount;
        },
        canSecure(value) {
            if (!value) this.reservation.option_bank = null;
        },
        "reservation.option_bank"(value) {
            if (value) this.reservation.send_confirmation = true;
        },
    },
};
</script>
